import type { ReactNode } from 'react';
import { Button } from '@mantine/core';
import { IconExternalLink, IconMapPin } from '@tabler/icons-react';
import { RIDES_SEX, type RidesCampusView, type RidesVehicleView } from '@actschurch/ccms-sdk';
import { formatDate, formatTime } from '@/lib/format';
import { PersonAvatar } from '@/components/rides/PersonAvatar';
import { PlateChip } from '@/components/rides/PlateChip';
import { campusAddressLine } from '@/components/rides/CampusPicker';
import { campusMapsUrl } from './WhereToMeet';
import { carWithColour } from './LiftCard';
import '@/styles/flows.css';

export type TicketTone = 'live' | 'ok' | 'muted';

interface Props {
  eventName: string;
  date: string | number;
  /** HH:mm leaving the pick-up campus (null = not known yet). */
  departureTime?: string | null;
  serviceTimeStart?: string | null;
  collectionName: string;
  dropOffName: string;
  /** Status words in the top band ("Confirmed", "Your driver is here"). */
  status?: ReactNode;
  /** live = brand band, ok = green band (driver here), muted = grey band (cancelled, past). */
  tone?: TicketTone;
  /** Rider tickets: the driver and the car to look for. */
  driver?: { name: string; photoUrl?: string | null; sexId?: number | null } | null;
  vehicle?: RidesVehicleView | null;
  /** Show the plate on its own big line (rider tickets). */
  bigPlate?: boolean;
  /** "Where to meet": the pick-up campus and its instructions, with Open in Maps. */
  meet?: { campus?: RidesCampusView | null; campusName: string } | null;
  /** Extra rows at the bottom of the stub ("Travelling: Just you"). */
  rows?: { k: ReactNode; v: ReactNode }[];
  bookingRef?: string | null;
  children?: ReactNode;
}

/**
 * The ride as a ticket: a brand band with the big departure time, the date and the route, a
 * perforated tear line, then the driver, the car with a big number plate and where to meet.
 * Used on the booking confirmation and both ride-day screens.
 */
export function RideTicket({ eventName, date, departureTime, serviceTimeStart, collectionName, dropOffName, status, tone = 'live', driver, vehicle, bigPlate, meet, rows, bookingRef, children }: Props) {
  const time = formatTime(departureTime ?? null);
  const instructions = meet?.campus?.pickupInstructions?.trim();
  const address = campusAddressLine(meet?.campus);
  const maps = campusMapsUrl(meet?.campus);
  return (
    <article className={`rides-ticket ${tone}`} aria-label={`Your ride to ${eventName}`}>
      <div className="band">
        <div className="toprow">
          <span className="event">{eventName}</span>
          {status && <span className="status">{status}</span>}
        </div>
        <div className="timerow">
          {time ? <span className="time">{time}</span> : <span className="time tbc">Time to be confirmed</span>}
          <span className="when">
            {time ? `Leaves ${collectionName}` : 'Leaving time'}
            <small>{formatDate(date)}</small>
          </span>
        </div>
        <div className="route" aria-label={`From ${collectionName} to ${dropOffName}`}>
          <span className="stop"><i aria-hidden="true" />{collectionName}</span>
          <span className="line" aria-hidden="true" />
          <span className="stop end"><i aria-hidden="true" />{dropOffName}</span>
        </div>
        {serviceTimeStart && <p className="svc">For the {formatTime(serviceTimeStart)} service</p>}
      </div>

      <div className="perf" aria-hidden="true" />

      <div className="stub">
        {driver && (
          <div className="driver">
            <PersonAvatar src={driver.photoUrl} name={driver.name} size={64} color={driver.sexId === RIDES_SEX.Female ? 'pink' : 'brand'} decorative />
            <div className="who">
              <span className="label">Your driver</span>
              <b>{driver.name}</b>
              {vehicle && <span className="car">{carWithColour(vehicle)}</span>}
            </div>
          </div>
        )}
        {vehicle && bigPlate && (
          <div className="plate">
            <span className="label">Look for this number plate</span>
            <PlateChip plate={vehicle.plate} large />
          </div>
        )}
        {meet && (
          <div className="meet">
            <IconMapPin size={22} aria-hidden="true" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <span className="label">Where to meet</span>
              <b>{meet.campus?.name ?? meet.campusName}</b>
              {instructions && <p>{instructions}</p>}
              {address && <p className="addr">{address}</p>}
            </div>
          </div>
        )}
        {meet && maps && (
          <Button component="a" href={maps} target="_blank" rel="noopener noreferrer" variant="default" fullWidth rightSection={<IconExternalLink size={18} aria-hidden="true" />}>
            Open in Maps
          </Button>
        )}
        {rows && rows.length > 0 && (
          <dl className="rows">
            {rows.map((r, i) => (
              <div key={i}>
                <dt>{r.k}</dt>
                <dd>{r.v}</dd>
              </div>
            ))}
          </dl>
        )}
        {children}
        {bookingRef && <p className="ref">Booking number <span>{bookingRef}</span></p>}
      </div>
    </article>
  );
}

/**
 * The confirmation moment: a check mark that draws itself inside a ring, with a few sparks.
 * Still (no animation) when the member asks for reduced motion.
 */
export function SuccessBurst({ label = 'Done' }: { label?: string }) {
  return (
    <div className="rides-burst" role="img" aria-label={label}>
      <span className="spark s1" aria-hidden="true" />
      <span className="spark s2" aria-hidden="true" />
      <span className="spark s3" aria-hidden="true" />
      <span className="spark s4" aria-hidden="true" />
      <span className="spark s5" aria-hidden="true" />
      <span className="spark s6" aria-hidden="true" />
      <svg viewBox="0 0 96 96" width="96" height="96" aria-hidden="true">
        <circle className="ring" cx="48" cy="48" r="40" />
        <path className="tick" d="M31 49.5 L43 61 L66 37" />
      </svg>
    </div>
  );
}

